import { CellData } from '@ddt-web/data-service';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';

import { MDSScenarioCategory, ScenarioHeaderConfig } from '../models/scenarios.interface';

export class MdsScenarioUtil {
	public static readonly NO_MDS_HEADER_DEFAULT_GROUP_NAME = '-';

	/**
	 * Checks if the provided scenario id is part of an mds group and returns the group name.
	 * If the scenario is not part of an mds group, undefined is returned.
	 * If mdsGroups is nil, undefined is returned.
	 *
	 * @param {string} scenarioId
	 * @param {Array<MDSScenarioCategory>} mdsGroups
	 * @returns group name if scenario is part of mds group, undefined otherwise
	 */
	public static getMdsGroupName(scenarioId: string, mdsGroups: MDSScenarioCategory[]): string {
		let groupName;

		if (!isNil(mdsGroups)) {
			const mdsGroup = mdsGroups.filter(group => group.scenarios.map(scenario => scenario.id).includes(scenarioId)).pop();
			groupName = get(mdsGroup, 'groupName');
		}

		return groupName;
	}

	/**
	 * Checks if the provided scenario id is part of an mds group.
	 * If mdsGroups is nil, false is returned.
	 *
	 * @param {string} scenarioId
	 * @param {Array<MDSScenarioCategory>} mdsGroups
	 * @returns true if scenario is part of mds group, false otherwise
	 */
	public static isMdsScenario(scenarioId: string, mdsGroups: MDSScenarioCategory[]): boolean {
		let isMdsScenario = false;

		if (!isNil(mdsGroups)) {
			const mdsGroup = mdsGroups.filter(group => group.scenarios.map(scenario => scenario.id).includes(scenarioId)).pop();
			isMdsScenario = !isNil(mdsGroup);
		}

		return isMdsScenario;
	}

	/**
	 * Returns the mds scenarios with valid scenarios. A scenario is valid if:
	 * - exists in allScenarios
	 * - exists in filteredScenarios - if provided
	 *
	 * If there are no valid scenarios for an mds group, the mds group is filtered out.
	 * If filteredScenarios is empty, empty array is returned.
	 * If the mdsScenarios or allScenarios is nil, empty array is returned.
	 *
	 * @param {Array<MDSScenarioCategory>} mdsScenarios mds scenarios config
	 * @param {Array<CellData>} allScenarios all valid scenarios we have data for
	 * @param {Array<CellData>} filteredScenarios optional param, filtered scenarios array
	 * @returns mds scenarios with valid scenarios, [] otherwise
	 */
	public static getValidMdsScenarios(mdsScenarios: MDSScenarioCategory[], allScenarios: CellData[], filteredScenarios?: CellData[]): MDSScenarioCategory[] {
		const validMdsScenarios: MDSScenarioCategory[] = [];

		if (!isNil(mdsScenarios) && !isNil(allScenarios) && !isEmpty(mdsScenarios)) {
			const validFilteredScenarios = isNil(filteredScenarios) ? allScenarios : filteredScenarios;
			const validFilteredScenariosIds: string[] = validFilteredScenarios.map(scenario => scenario.rawValue as string);
			const allScenariosIds = allScenarios.map(scenario => scenario.rawValue as string);

			if (!isEmpty(validFilteredScenariosIds)) {
				const filteredMdsScenarios = mdsScenarios.map(group => {
					const validScenarios = get(group, 'scenarios', []).filter(mdsScenario => allScenariosIds.includes(mdsScenario.id)).map(mdsScenario => {
						const scenario = allScenarios.find(scenario => scenario.rawValue === mdsScenario.id);
						const name = get(scenario, 'formattedValue', mdsScenario.name);

						return { id: mdsScenario.id, name: name, description: mdsScenario.description };
					});
					const isMdsWithFilteredScenario = validScenarios.some(scenario => validFilteredScenariosIds.includes(scenario.id));
					const scenarios = isMdsWithFilteredScenario ? validScenarios : [];

					return { groupId: group.groupId, groupName: group.groupName, scenarios: scenarios };
				}).filter(group => !isEmpty(group.scenarios));

				validMdsScenarios.push(...filteredMdsScenarios);
			}
		}

		return validMdsScenarios;
	}

	/**
	 * Returns the mds scenario header array based on the provided table header and mds scenarios.
	 * If tableHeaders or mdsScenarios is nil, empty array is returned.
	 * If tableHeaders does not have any mds scenarios, empty array is returned.
	 * If table header has an mds scenario, a ScenarioHeaderConfig object id added with name as groupName.
	 * If a table header is not part of mds scenarios, a default ScenarioHeaderConfig object is added (name as default value and noOfScenarios as 1).
	 * If shouldAddIncludeYieldHeader is true, a default ScenarioHeaderConfig object is added at the beginning of the array.
	 * If shouldAddDefaultValue is true, default value is set to ScenarioUtils.NO_MDS_HEADER_DEFAULT_GROUP_NAME, otherwise it is ''.
	 *
	 * @param {Array<CellData>} tableHeaders table headers of selected scenarios
	 * @param {Array<MDSScenarioCategory>} mdsScenarios valid mds scenarios (with group name and scenarios)
	 * @param {boolean} shouldAddDefaultValue
	 * @param {boolean} shouldAddIncludeYieldHeader
	 * @return {Array<ScenarioHeaderConfig>} mds header array
	 */
	public static getMdsGroupHeader(
		tableHeaders: Array<CellData>,
		mdsScenarios: MDSScenarioCategory[],
		shouldAddIncludeYieldHeader: boolean = false,
		shouldAddDefaultValue: boolean = false,
		shouldIncludeAllMdsGroupDetails: boolean = false
	): ScenarioHeaderConfig[] {

		const headerArr: ScenarioHeaderConfig[] = [];

		if (!isNil(tableHeaders) && !isNil(mdsScenarios)) {
			const defaultValue = shouldAddDefaultValue ? MdsScenarioUtil.NO_MDS_HEADER_DEFAULT_GROUP_NAME : '';
			const noMdsScenarioHeader = { name: defaultValue, noOfScenarios: 1 };

			const hasMdsScenario = tableHeaders.some(cell => mdsScenarios.some(group => group.scenarios.map(scenario => scenario.id).includes(cell.rawValue as string)));

			if (hasMdsScenario) {
				tableHeaders.forEach((header: CellData, index: number) => {
					const mdsGroup = mdsScenarios.filter(group => group.scenarios.map(scenario => scenario.id).includes(header.rawValue as string)).pop();

					if (mdsGroup) {
						const groupName = get(mdsGroup, 'groupName', defaultValue);
						const noOfScenarios = mdsGroup.scenarios.length;

						let headerObject = { name: groupName, noOfScenarios: noOfScenarios };
						if (shouldIncludeAllMdsGroupDetails) {
							headerObject = Object.assign({}, headerObject, { groupId: mdsGroup.groupId, scenarios: mdsGroup.scenarios });
						}
						// check if the group name has already been added
						if (!headerArr.map(scenario => scenario.name).includes(groupName)) {
							headerArr.push(headerObject);
						}

					} else {
						headerArr.push(noMdsScenarioHeader);
					}
				});

				if (shouldAddIncludeYieldHeader) {
					headerArr.unshift(noMdsScenarioHeader);
				}
			}
		}

		return headerArr;
	}

	/**
	 * Orders the mds scenarios in the order they were selected based on the provided selected scenarios.
	 * If selectedScenarios or mdsGroups is nil, empty array is returned.
	 *
	 * @param {Array<MDSScenarioCategory>} mdsGroups
	 * @param {Array<CellData>} selectedScenarios
	 * @return {Array<ScenarioHeaderConfig>} mds scenarios in the order they were selected
	 */
	public static getOrderedMdsGroups(mdsGroups: MDSScenarioCategory[], selectedScenarios: CellData[]): MDSScenarioCategory[] {
		const orderedMdsScenarios: MDSScenarioCategory[] = [];

		if (!isNil(mdsGroups) && !isNil(selectedScenarios)) {
			selectedScenarios.forEach(selectedScenario => {
				const mdsGroup = mdsGroups.filter(group => {
					const scenarios = get(group, 'scenarios', []);
					return scenarios.map(scenario => scenario.id).includes(selectedScenario.rawValue as string);
				}).pop();

				if (mdsGroup) {
					const groupName = mdsGroup.groupName;

					if (groupName) {
						if (!orderedMdsScenarios.map(group => group.groupName).includes(groupName)) {
							orderedMdsScenarios.push(mdsGroup);
						}
					}
				}
			});
		}

		return orderedMdsScenarios;
	}
}
